import { useState, useRef, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Typography } from "@altimateai/lego";
import { mockSessions, mockSessionMessages, USE_MOCK_DATA } from "./mockData";
import {
  useSessionDetail,
  useTransitionSession,
  useSendMessage,
  useSessionSSE,
  useWorkflowAgents,
  useUpdateSession,
} from "./useWorkflowQueries";
import { sendSystemMessage } from "./workflowApi";
import type { SessionStatus, SessionMessage } from "./types";
import { parseMessageContent } from "./WorkflowSessionMessages";
import {
  buildSlackUrl,
  getTransitions,
  statusStyleMap,
} from "./sessionDetailHelpers";
import SessionConversationPanel from "./SessionConversationPanel";
import SessionMetaGrid from "./SessionMetaGrid";
import styles from "./workflow.module.scss";

const WorkflowSessionDetail = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [messageInput, setMessageInput] = useState("");
  const [showSystemMessages, setShowSystemMessages] = useState(false);
  const [agentAssignFlash, setAgentAssignFlash] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const [syncAgo, setSyncAgo] = useState("");
  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const lastMessageCountRef = useRef(0);

  const {
    data: apiDetail,
    isLoading,
    dataUpdatedAt,
  } = useSessionDetail(USE_MOCK_DATA ? null : (sessionId ?? null));

  useEffect(() => {
    if (dataUpdatedAt) setLastSyncedAt(new Date(dataUpdatedAt));
  }, [dataUpdatedAt]);

  useEffect(() => {
    const tick = () => {
      if (!lastSyncedAt) {
        setSyncAgo("");
        return;
      }
      const secs = Math.floor((Date.now() - lastSyncedAt.getTime()) / 1000);
      setSyncAgo(secs < 5 ? "just now" : `${secs}s ago`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lastSyncedAt]);

  const transitionMutation = useTransitionSession();
  const sendMessageMutation = useSendMessage();
  const { data: agents } = useWorkflowAgents();
  const updateSessionMutation = useUpdateSession();

  useSessionSSE(USE_MOCK_DATA ? null : (sessionId ?? null));

  const mockSession = USE_MOCK_DATA ? mockSessions.find(s => s.id === sessionId) : null;
  const mockMessages = USE_MOCK_DATA ? (mockSessionMessages[sessionId ?? ""] ?? []) : [];
  const session = USE_MOCK_DATA ? mockSession : apiDetail?.session;
  const allMessages: SessionMessage[] = USE_MOCK_DATA ? mockMessages : (apiDetail?.messages ?? []);

  const isHiddenRole = (role: SessionMessage["role"]) =>
    role === "system" || role === "tool" || role === "toolResult";
  const visibleMessages = allMessages.filter(m => !isHiddenRole(m.role) || showSystemMessages);

  const isActiveStatus = session?.status === "active";
  const hiddenCount = allMessages.length - visibleMessages.length;

  const participants = (() => {
    const seen = new Map<string, { name: string; role: string; isSlack: boolean }>();
    seen.set("assistant", { name: "Altimate AI", role: "assistant", isSlack: false });
    for (const m of allMessages) {
      if (m.role === "system" || m.role === "tool" || m.role === "toolResult") continue;
      if (m.role === "assistant") continue;
      const parsed = parseMessageContent(m.content);
      const name = parsed.sender || "Web User";
      const isSlack = parsed.isSlack || false;
      if (!seen.has(name)) {
        seen.set(name, { name, role: m.role, isSlack });
      }
    }
    return Array.from(seen.values());
  })();

  const isWaitingForReply =
    sendMessageMutation.isPending ||
    (allMessages.length > 0 && allMessages[allMessages.length - 1]?.role === "user");

  useEffect(() => {
    const newCount = visibleMessages.length;
    const prevCount = lastMessageCountRef.current;
    lastMessageCountRef.current = newCount;

    if (newCount <= prevCount) return;

    const container = chatContainerRef.current;
    if (!container) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom < 150 || prevCount === 0) {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [visibleMessages]);

  const handleSendMessage = useCallback(() => {
    const text = messageInput.trim();
    if (!text || !sessionId) return;
    setMessageInput("");
    sendMessageMutation.mutate({ sessionId, content: text });
  }, [messageInput, sessionId, sendMessageMutation]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleTransition = (newStatus: SessionStatus) => {
    if (!sessionId) return;
    transitionMutation.mutate({ sessionId, status: newStatus });
  };

  const handleAgentAssign = (agentId: string) => {
    if (!sessionId) return;
    const value = agentId || null;
    const prevAgentName = session?.current_agent_id
      ? ((agents ?? []).find(a => a.id === session.current_agent_id)?.name ?? "previous agent")
      : null;
    const newAgentName = agentId
      ? ((agents ?? []).find(a => a.id === agentId)?.name ?? "Unknown Agent")
      : "Unassigned (Generic)";

    updateSessionMutation.mutate(
      { sessionId, data: { current_agent_id: value } },
      {
        onSuccess: () => {
          setAgentAssignFlash(true);
          setTimeout(() => setAgentAssignFlash(false), 1500);
          const reassignNote = prevAgentName
            ? `Agent reassigned from **${prevAgentName}** to **${newAgentName}**`
            : `Agent assigned: **${newAgentName}**`;
          sendSystemMessage(sessionId, reassignNote).catch(() => {});
        },
      }
    );
  };

  if (!USE_MOCK_DATA && isLoading) {
    return (
      <div className={styles.sessionDetail}>
        <div className={styles.loadingState}>
          <div className={styles.spinner} />
          <Typography variant="body">Loading session...</Typography>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className={styles.sessionDetail}>
        <div className={styles.emptyState}>
          <Typography variant="body">Session not found.</Typography>
        </div>
      </div>
    );
  }

  const transitions = getTransitions(session.status);

  return (
    <div className={styles.sessionDetail}>
      <div className={styles.sessionDetailHeader}>
        <div className={styles.sessionTitle}>
          <h2>{session.title ?? `Session ${(sessionId ?? "").slice(0, 8)}`}</h2>
          <div className={styles.sessionBadges}>
            <span className={`${styles.statusBadge} ${styles[statusStyleMap[session.status]]}`}>
              {session.status.replace(/_/g, " ")}
            </span>
            {isActiveStatus && <span className={styles.liveBadge}>LIVE</span>}
          </div>
          {session.metadata?.slack_channel_id && session.metadata?.slack_thread_ts && (
            <a
              href={buildSlackUrl(
                session.metadata.slack_channel_id,
                session.metadata.slack_thread_ts
              )}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.slackLink}
            >
              View in Slack
            </a>
          )}
        </div>

        <div className={styles.sessionActions}>
          {transitions.map(t => (
            <button
              key={t.status}
              type="button"
              className={`${styles.transitionBtn} ${t.status === "cancelled" ? styles.transitionBtnDanger : styles.transitionBtnPrimary}`}
              onClick={() => handleTransition(t.status)}
              disabled={transitionMutation.isPending}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <SessionMetaGrid
        session={session}
        agents={agents ?? []}
        agentAssignFlash={agentAssignFlash}
        isAgentSaving={updateSessionMutation.isPending}
        onAgentAssign={handleAgentAssign}
      />

      <SessionConversationPanel
        visibleMessages={visibleMessages}
        participants={participants}
        syncAgo={syncAgo}
        hiddenCount={hiddenCount}
        showSystemMessages={showSystemMessages}
        onToggleSystemMessages={() => setShowSystemMessages(prev => !prev)}
        isWaitingForReply={isWaitingForReply}
        messageInput={messageInput}
        onMessageChange={setMessageInput}
        onKeyDown={handleKeyDown}
        onSendMessage={handleSendMessage}
        isSending={sendMessageMutation.isPending}
        chatContainerRef={chatContainerRef}
        chatEndRef={chatEndRef}
      />
    </div>
  );
};

export default WorkflowSessionDetail;
